import type { FUNNEL_TYPE } from '../../enum/constants';
import type { CardTimelineAuthor } from '../board-v3/board-v3.types';

/** Tasks API (`/task`): the fields the app uses, as they come */

/** `REVIEW` is used only by a task that requires a review of its creator */
export const TASK_STATUSES = ['NEW', 'IN_PROGRESS', 'REVIEW', 'POSTPONED', 'CANCELLED', 'DONE'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_PRIORITIES = ['LOW', 'NORMAL', 'HIGH'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/** A lead and a deal are both cards: the type of the funnel of the card tells which one it is */
export const TASK_LINK_TYPES = ['card', 'contact', 'company'] as const;
export type TaskLinkType = (typeof TASK_LINK_TYPES)[number];

export interface TaskLink {
  entity_type: TaskLinkType;
  entity_id: string;
}

export interface TaskReminder {
  id: string;
  remind_at: string;
  /** Set for a reminder that follows the deadline, null for one at an exact time */
  before_deadline_minutes: number | null;
  /** When the reminder was sent, null until then */
  sent_at: string | null;
}

export interface Task {
  id: string;
  title: string;
  description: string | null;
  /** The creator of the task, a user id */
  created_by: string;
  /** The executor, a user id */
  assignee_id: string;
  observer_ids: string[];
  deadline: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  /** The executor sends the task to review and the creator accepts it, instead of the executor closing it */
  requires_review: boolean;
  /** Worked out by the backend: the deadline has passed and the task is neither done nor canceled */
  is_overdue: boolean;
  links: TaskLink[];
  reminders: TaskReminder[];
  created_at: string;
  updated_at: string | null;
}

export interface TasksPage {
  items: Task[];
  total: number;
  size: number;
  page: number;
}

/** `deadline_from` and `deadline_to` are inclusive ISO bounds; a task without a deadline is not in such a list */
export interface TasksQuery {
  assignee_id?: string | null;
  created_by?: string | null;
  status?: readonly TaskStatus[];
  priority?: readonly TaskPriority[];
  /** `true`: only the overdue tasks, `false`: only the others, not set: all */
  overdue?: boolean | null;
  deadline_from?: string | null;
  deadline_to?: string | null;
  /** The two are sent together: the tasks linked to that entity */
  entity_type?: TaskLinkType;
  entity_id?: string;
  page?: number;
  /** At most 100 */
  take?: number;
}

/** Exactly one of the two: the time of the reminder, or the minutes before the deadline (the task needs one) */
export interface TaskReminderPayload {
  remind_at?: string;
  before_deadline_minutes?: number;
}

export interface CreateTaskPayload {
  title: string;
  description?: string | null;
  /** The creator is the executor when it is not sent */
  assignee_id?: string;
  observer_ids?: string[];
  /** Precise to the minute */
  deadline?: string | null;
  priority?: TaskPriority;
  requires_review?: boolean;
  links?: TaskLink[];
  reminders?: TaskReminderPayload[];
}

/** Only the sent fields change; `observer_ids` and `links` replace the whole list, `deadline: null` removes it */
export type UpdateTaskPayload = Partial<Omit<CreateTaskPayload, 'reminders'>>;

/** The events of a task come in the format of the timelines of cards, contacts and companies */
export type TaskTimelineEventType = 'TASK' | 'COMMENT';
export type TaskTimelineSubType = 'STATUS_CHANGE' | 'DEADLINE_CHANGE';

export interface TaskTimelineEvent {
  id: string;
  event_type: TaskTimelineEventType;
  action: string;
  /** Only the updates of a task have one */
  sub_type?: TaskTimelineSubType | null;
  created_at: string;
  created_by?: string | null;
  /** Who did it */
  created_by_user?: CardTimelineAuthor | null;
  x_data: {
    status?: { old: TaskStatus; new: TaskStatus };
    /** ISO or null: the deadline before and after */
    deadline?: { old: string | null; new: string | null };
    /** The text of a comment */
    comment?: string;
  } | null;
}

// #region screens

/** An employee as the task screens use them: the pickers offer the active ones, a name resolves for the others too */
export interface TaskUser {
  id: string;
  name: string;
  avatarUrl?: string;
  isActive: boolean;
}

/** A link with the name of what it points to, for the screens that show or pick links */
export interface ResolvedTaskLink extends TaskLink {
  name: string;
  /** The funnel type of a card: a lead and a deal open in different boards */
  funnelType?: FUNNEL_TYPE;
  /** It could not be loaded: it is gone or out of reach. The link itself stays valid until it is removed */
  missing?: boolean;
}

/** A search result of the link picker */
export interface TaskLinkTarget extends ResolvedTaskLink {
  /** What tells apart records with the same name: the funnel of a card, the phone or the email of a contact */
  subtitle?: string;
}

/** What the form of a new task starts with */
export interface TaskFormPayload {
  /** E.g. the card the task is created from */
  links?: ResolvedTaskLink[];
  /** ISO, e.g. the end of today for a task created from the tasks of the day */
  deadline?: string;
}

/** What the current user has to do by the end of today */
export interface TodayTasks {
  /** The tasks that are overdue or due today, the nearest deadline first */
  items: Task[];
  /** How many tasks there are in all: more than `items` when the lists were cut at the biggest page */
  total: number;
}

export interface TaskDetailPayload {
  taskId: string;
  /** Off while a card detail is open: a second card detail would replace the shared `BoardService.card` of the first */
  canOpenCards: boolean;
}

export interface TaskDetailResult {
  /** The task was changed: saved, moved to another status, got comments or reminders, or was deleted */
  changed: boolean;
  deleted?: boolean;
}

// #endregion
